// Components
import { FhirStatus, Proof, StatusAlert, StatusIcon, Thread, Title } from "@fyrstain/fhir-front-library";
import PandoraPage from '../../components/PandoraPage/PandoraPage';
import TestOperationSection from 'src/components/TestReport/TestOperationSection/TestOperationSection';
import TestReportResultAlert from '../../components/TestReport/TestReportResultAlert/TestReportResultAlert';
// Authentication
import UserService from "src/services/UserService";
// Dayjs
import dayjs from 'dayjs';
// FHIR
import Client from 'fhir-kit-client';
import { Bundle, TestReport, TestReportSetupAction, TestReportTeardownAction, TestReportTestAction, TestScript } from "fhir/r5";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback, useState } from "react";
// React Bootstrap
import { Card, Form } from "react-bootstrap";
// React Router
import { useNavigate, useParams } from "react-router-dom";
import { useOnMount } from "src/hooks/useOnMount";
// Style
import styles from "./testReportDetails.module.css";
// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { toPublicUrl } from "src/services/PublicUrl";

const TestReportDetails: FunctionComponent = () => {

    /////////////////////////////////////////////
    //                 State                   //
    /////////////////////////////////////////////

    const { testReportId } = useParams();
    const [tests, setTests] = useState([] as { toggle: boolean, id: string, name: string, description: string, operations: { operation: TestReportTestAction, asserts: TestReportTestAction[] }[] }[]);
    const [setups, setSetups] = useState([] as { toggle: boolean, operation: TestReportSetupAction, asserts: TestReportSetupAction[] }[]);
    const [teardowns, setTeardowns] = useState([] as { toggle: boolean, id: string, operation: TestReportTeardownAction }[]);
    const [report, setReport] = useState({} as TestReport);
    const [script, setScript] = useState({} as TestScript);
    const [loading, setLoading] = useState(false);
    const [isCreatingTest, setIsCreatingTest] = useState(false);

    const [serverLabel, setServerLabel] = useState("");
    const [clientLabel, setClientLabel] = useState("");
    const [proxychannel, setProxyChannel] = useState("");

    const [isManualTest, setIsManualTest] = useState(false);

    /////////////////////////////////////////////
    //                Constants                //
    /////////////////////////////////////////////

    const reactAppFhirUrl = process.env.REACT_APP_FHIR_URL ?? 'https://integ.fyrstain.com/r5-data';

    /////////////////////////////////////////////
    //                  Client                 //
    /////////////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    });

    /////////////////////////////////////////////
    //                 Functions               //
    /////////////////////////////////////////////

    const navigate = useNavigate();

    /**
     * Function to get the detail of the operation.
     * 
     * @param detail    the detail to get.
     */
    const onDetail: (detail: string | undefined) => Promise<string> = async (detail) => {
        let url = '';
        if (detail) {
            if (detail.startsWith("Bundle/")) {
                const bundleId = detail.split('/')[1];
                try {
                    const bundle = await fhirClient.read({
                        resourceType: 'Bundle',
                        id: bundleId,
                    });
                    const identifier = bundle?.identifier;
                    if (identifier?.system === "http://isis.com/test/evaluation-report") {
                        url = "/EvaluationReport/" + bundleId;
                    } else {
                        url = "/ValidationReport/" + bundleId;
                    }
                } catch (error) {
                    onError();
                }
            } else if (detail.startsWith("Parameters/")) {
                url = "/ExchangeDetails/" + detail.split('/')[1];
            }
        }
        return url ? toPublicUrl(url) : "";
    };

    /**
     * Function to navigate to the error page.
     */
    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    /**
     * Retrieve setups operations and asserts to populate the table.
     * 
     * @param report    the report to the Test report.
     */
    function retrieveSetups(report: TestReport) {
        const result: { toggle: boolean, operation: TestReportSetupAction, asserts: TestReportSetupAction[] }[] = [];
        report.setup?.action.filter(a => a.operation).forEach(a => {
            const isPass = a.operation?.result === 'pass';
            result.push({ "toggle": !isPass, "operation": a, asserts: report.setup?.action.filter(action => action.assert?.id?.includes(a.operation?.id ?? '')) ?? [] })
        })
        return result;
    }

    /**
     * Retrieve operations and asserts tests to populate the table.
     * 
     * @param report    the report to the Test report.
     */
    function retrieveTests(report: TestReport) {
        const result: { toggle: boolean, id: string, name: string, description: string, operations: { operation: TestReportTestAction, asserts: TestReportTestAction[] }[] }[] = [];
        const testMap = new Map<string, { toggle: boolean, id: string, name: string, description: string, operations: { operation: TestReportTestAction, asserts: TestReportTestAction[] }[] }>();
        if (!report.test) {
            report.test = [];
        }
        report.test.forEach(test => {
            const testId = test.id ?? 'N/A';
            const testName = test.name ?? '';
            const testDescription = test.description ?? '';
            test.action.forEach(action => {
                if (action.operation) {
                    const isPass = action.operation.result === 'pass';
                    const operationId = action.operation.id ?? 'N/A';
                    const asserts = test.action.filter(a => a.assert?.id?.includes(operationId)) ?? [];
                    if (testMap.has(testId)) {
                        const existingTest = testMap.get(testId);
                        if (existingTest) {
                            existingTest.operations.push({ operation: action, asserts });
                        }
                    } else {
                        testMap.set(testId, { toggle: !isPass, id: testId, name: testName, description: testDescription, operations: [{ operation: action, asserts }] });
                    }
                }
            });
        });
        testMap.forEach(test => result.push(test));
        return result;
    }

    /**
     * Retrieve teardowns operations to populate the table.
     * 
     * @param report 
     */
    function retrieveTeardowns(report: TestReport) {
        const result: { toggle: boolean, id: string, operation: TestReportTeardownAction }[] = [];
        report.teardown?.action.filter(a => a.operation).forEach(a => {
            const isPass = a.operation?.result === 'pass';
            result.push({
                "toggle": !isPass, "id": a.operation?.id ?? 'N/A', "operation": a

            })
        })
        return result;
    }

    /**
     * Read the report from the back
     * 
     * @param testReportId
     * @returns 
     */
    async function readReport(testReportId: string) {
        const response = await fhirClient.read({
            resourceType: 'TestReport',
            id: testReportId,
        });
        return response as TestReport;
    };

    /**
    * Load Report from the back to populate the table.
    * 
    * @returns the promise of a Test report.
    */
    async function loadReport() {
        setLoading(true);
        try {
            const testReport = await readReport(testReportId ?? '');
            const automatedTestExtension = testReport.extension?.find(ext => ext.url === "http://isis.com/test/StructureDefinition/EXT-AutomatedTest");
            if (automatedTestExtension) {
                setIsManualTest(!automatedTestExtension.valueBoolean);
            }
            setReport(testReport);
            setSetups(retrieveSetups(testReport));
            setTests(retrieveTests(testReport));
            setTeardowns(retrieveTeardowns(testReport));
            const response2 = await fhirClient.search({
                resourceType: 'TestScript',
                searchParams: {
                    "url": testReport.testScript,
                }
            });
            const bundle: Bundle = response2 as Bundle;
            const script: TestScript = bundle.entry?.at(0)?.resource as TestScript;
            if (script) {
                setScript(script);
            }
            setClientLabel(testReport.participant?.find(participant => participant.type === "client")?.display ?? "N/A");
            setServerLabel(testReport.participant?.find(participant => participant.type === "server")?.display ?? "N/A");
            setProxyChannel(testReport.extension?.find(extension => extension.url === "ProxyChannelName")?.valueString ?? "N/A");
            setLoading(false);
        } catch (error) {
            onError();
        }
    }

    /**
     * Get the existing operation IDs
     * 
     */
    const getExistingOperationIds = () => {
        const setupIds = setups.flatMap(setup => setup.operation.operation?.id ? [setup.operation.operation.id] : []);
        const teardownIds = teardowns.flatMap(teardown => teardown.operation.operation?.id ? [teardown.operation.operation.id] : []);
        const testOperationIds = tests.flatMap(test => test.operations.flatMap(op => op.operation.operation?.id ? [op.operation.operation.id] : []));
        return { setup: setupIds, teardown: teardownIds, test: testOperationIds };
    };

    /**
     * Variable to store the existing operation IDs
     */
    const existingOperationIds = getExistingOperationIds();

    /**
    * Generate a new test ID
    * 
    * @returns {string} The new test ID
    */
    const generateNewTestId = (): string => {
        const existingTestIds = report?.test?.map((test: any) => test.id) || [];
        const testIdPattern = /^TEST-(\d+)$/;
        const existingIndexes = existingTestIds
            .map((id: string) => {
                const match = id.match(testIdPattern);
                return match ? parseInt(match[1], 10) : 0;
            })
            .filter((index: any) => index > 0);
        const maxIndex = existingIndexes.length > 0 ? Math.max(...existingIndexes) : 0;
        const newTestId = `TEST-${String(maxIndex + 1).padStart(2, '0')}`;
        return newTestId;
    };

    /**
     * Handle the addition of a new test.
     * 
     * @param newTest 
     * @returns 
     */
    const handleAddNewTest = () => {
        if (isCreatingTest) {
            alert(i18n.t('infomessage.creatingtest'));
            return;
        }
        if (!report.test) {
            report.test = [];
        }
        const newTestId = generateNewTestId();
        const newTest = {
            id: newTestId,
            name: '',
            description: '',
            operations: [],
            toggle: true,
        };
        setTests(prevTests => {
            return [...prevTests, newTest];
        });
        setIsCreatingTest(true);
    };

    /**
     * Handle the TestReport update
     * 
     * @param updatedTestReport 
     */
    const handleUpdateTestReport = (updatedTestReport: TestReport) => {
        setReport(updatedTestReport);
    };

    /**
     * handle the addition of a setup operation after the addition of a row
     * 
     * @param newSetup 
     */
    const handleAddSetup = (newSetup: any) => {
        const setupOperations = retrieveSetups(newSetup);
        if (setupOperations.length > 0) {
            const lastIndex = setupOperations.length - 1;
            setSetups(prevSetups => [...prevSetups, setupOperations[lastIndex]]);
        }
    };

    /**
     * handle the addition of a test operation after the addition of a row
     * 
     * @param newTest 
     */
    const handleAddTest = (newTest: any) => {
        const testOperations = retrieveTests(newTest);
        if (testOperations.length > 0) {
            testOperations.forEach(result => {
                if (result && result.operations) {
                    const newTest = result.operations[result.operations.length - 1];
                    const newTestId = newTest.operation?.operation?.id;
                    const testIndex = tests.findIndex(test => test.id === result.id);
                    if (testIndex !== -1) {
                        const operationIndex = tests[testIndex].operations.findIndex(op => op.operation.operation?.id === newTestId);
                        if (operationIndex === -1) {
                            setTests(prevTests => {
                                const newTests = [...prevTests];
                                newTests[testIndex].operations.push(newTest);
                                return newTests;
                            });
                        } else {
                            setTests(prevTests => {
                                const newTests = [...prevTests];
                                newTests[testIndex].operations[operationIndex] = newTest;
                                return newTests;
                            });
                        }
                    } else {
                        setTests(prevTests => [...prevTests, result]);
                    }
                }
            });
        }
    };


    /**
     * handle the addition of a teardown operation after the addition of a row
     * 
     * @param newTeardown 
     */
    const handleAddTeardown = (newTeardown: any) => {
        const teardownOperations = retrieveTeardowns(newTeardown);
        if (teardownOperations.length > 0) {
            const lastIndex = teardownOperations.length - 1;
            setTeardowns(prevTeardowns => [...prevTeardowns, teardownOperations[lastIndex]]);
        }
    };

    /**
     * handle the addition of a setup assert after the addition of a row
     * 
     * @param newSetup 
     */
    const handleAddSetupAssert = (newSetup: any) => {
        const setupResults = retrieveSetups(newSetup);
        if (setupResults.length > 0) {
            setupResults.forEach(result => {
                if (result && result.operation && result.operation.operation) {
                    const newAssert = result.asserts[result.asserts.length - 1];
                    const newAssertId = newAssert?.assert?.id;
                    const setupIndex = setups.findIndex(setup => setup.operation.operation?.id === result.operation.operation?.id);
                    if (setupIndex !== -1) {
                        const assertIndex = setups[setupIndex].asserts.findIndex(assert => assert.assert?.id === newAssertId);
                        if (assertIndex === -1) {
                            setSetups(prevSetups => {
                                const newSetups = [...prevSetups];
                                newSetups[setupIndex].asserts.push(newAssert);
                                return newSetups;
                            });
                        } else {
                            setSetups(prevSetups => {
                                const newSetups = [...prevSetups];
                                newSetups[setupIndex].asserts[assertIndex] = newAssert;
                                return newSetups;
                            });
                        }
                    }
                }
            });
        }
    };

    /**
     * handle the addition of a test assert after the addition of a row
     * 
     * @param newTest 
     */
    const handleAddTestAssert = (newTest: any) => {
        const testResults = retrieveTests(newTest);
        if (testResults.length > 0) {
            testResults.forEach(result => {
                if (result && result.operations) {
                    result.operations.forEach(operation => {
                        if (operation && operation.operation && operation.operation.operation) {
                            const newAssert = operation.asserts[operation.asserts.length - 1];
                            const newAssertId = newAssert?.assert?.id;
                            const testIndex = tests.findIndex(test => test.id === result.id);
                            if (testIndex !== -1) {
                                const operationIndex = tests[testIndex].operations.findIndex(op => op.operation.operation?.id === operation.operation.operation?.id);
                                if (operationIndex !== -1) {
                                    const assertIndex = tests[testIndex].operations[operationIndex].asserts.findIndex(assert => assert.assert?.id === newAssertId);
                                    if (assertIndex === -1) {
                                        setTests(prevTests => {
                                            const newTests = [...prevTests];
                                            newTests[testIndex].operations[operationIndex].asserts.push(newAssert);
                                            return newTests;
                                        });
                                    } else {
                                        setTests(prevTests => {
                                            const newTests = [...prevTests];
                                            newTests[testIndex].operations[operationIndex].asserts[assertIndex] = newAssert;
                                            return newTests;
                                        });
                                    }
                                }
                            }
                        }
                    });
                }
            });
        }
    }

    /////////////////////////////////////////////
    //                 Lifecycle               //
    /////////////////////////////////////////////

    /** 
     * Load the report when the page is loaded.
     * 
     */
    useOnMount(() => {
        void loadReport();
    });

    // Component to display the label value details
    const LabelValueDetail: FunctionComponent<{ label: string, value: any }> = ({ label, value }) => (
        <div className="d-flex align-items-center gap-2">
            <Form.Label className="fw-semibold me-2 mb-0">{label}:</Form.Label>
            <Form.Text>{String(value)}</Form.Text>
        </div>
    );

    /////////////////////////////////////////////
    //                Content                  //
    /////////////////////////////////////////////

    return (
        <PandoraPage
            titleKey={isManualTest ? 'title.manualtestreport' : 'title.testreport'}
            loading={loading}
            needsLogin={true}
        >
            <>
                <Card>
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                            <Title level={2} content={'Informations'} />
                            {isManualTest &&
                                <div className="d-flex justify-content-center align-items-center">
                                    <Proof
                                        serverUrl={reactAppFhirUrl}
                                        userIdentifier={UserService.getUsername()}
                                        onError={() => { }}
                                        testReport={report}
                                        testScript={script}
                                        testStep='label.generalproof'
                                        language={i18n.t}
                                        refreshTestReport={readReport}
                                    />
                                </div>
                            }
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <div className={`${styles.alert} mx-auto`}>
                            {isManualTest ? (
                                <TestReportResultAlert
                                    testReport={report}
                                />
                            ) : (
                                <StatusAlert
                                    status={FhirStatus[report.result as keyof typeof FhirStatus]}
                                >
                                    <div className="d-flex align-items-center">
                                        <StatusIcon
                                            status={FhirStatus[report.result as keyof typeof FhirStatus]}
                                        />
                                        <Form.Label className="fw-bold ms-2 mb-0">{report.id} / {report.name}</Form.Label>
                                    </div>
                                </StatusAlert>
                            )}
                        </div>
                        <div className="d-flex flex-column ms-4 gap-2">
                            <LabelValueDetail
                                label={i18n.t('label.generaldescription')}
                                value={script.description}
                            />
                            <LabelValueDetail
                                label={i18n.t('label.started')}
                                value={dayjs(report.issued).fromNow()}
                            />
                            <LabelValueDetail
                                label={i18n.t('label.duration')}
                                value={`${Math.round(dayjs(report.meta?.lastUpdated).diff(report.issued, "second", true))} ${i18n.t('label.seconds')}`}
                            />
                            <LabelValueDetail
                                label={i18n.t('label.client')}
                                value={clientLabel}
                            />
                            <LabelValueDetail
                                label={i18n.t('label.server')}
                                value={serverLabel}
                            />
                            <LabelValueDetail
                                label={i18n.t('label.proxychannel')}
                                value={proxychannel}
                            />
                        </div>
                    </Card.Body>
                </Card>
                <div className="section">
                    <Thread
                        serverUrl={reactAppFhirUrl}
                        userIdentifier={UserService.getUsername()}
                        onError={() => onError}
                        testReport={report}
                        testScript={script}
                        testStep="label.generalcomment"
                        language={i18n.t}
                        refreshTestReport={readReport}
                    />
                </div>

                <TestOperationSection
                    operations={setups}
                    asserts={setups.map(setup => setup.asserts).flat()}
                    testReport={report}
                    testScript={script}
                    testStepHeaderOperation='Setup'
                    testStepOperation={(setup: any) => setup.operation.operation?.id ?? ''}
                    testStepAssert={(assert: any) => assert.assert?.id ?? ''}
                    isManualTest={isManualTest}
                    titleSection='Setup'
                    addTestOperation='setupOperation'
                    addTestAssert='setupAssert'
                    onError={onError}
                    onDetail={onDetail}
                    onUpdateTestReport={handleUpdateTestReport}
                    onAddOperation={handleAddSetup}
                    onAddAssert={handleAddSetupAssert}
                    showAddFaPlusIcon={true}
                    existingOperationIds={existingOperationIds}
                    refreshTestReport={readReport}
                />

                <Card className="section">
                    <Card.Header className={`${styles.noOperationCard} d-flex flex-wrap align-items-center`}>
                        <div className="d-flex flex-wrap align-items-start align-items-center gap-4">
                            <Title
                                level={2}
                                content={'Tests'}
                            />
                            {isManualTest ? (
                                <FontAwesomeIcon
                                    className="actionIcon"
                                    icon={faPlus}
                                    title={`${i18n.t('general.addoperation')} test`}
                                    onClick={() => handleAddNewTest()}
                                />
                            ) : (!isManualTest && tests.length === 0) && (
                                <span className="fw-bold">({i18n.t('infomessage.nooperation')})</span>
                            )}
                        </div>
                    </Card.Header>
                    {tests.length > 0 && (
                        <Card.Body>
                            {tests.map((test, index) => (
                                <TestOperationSection
                                    key={test.id || index}
                                    operations={test.operations}
                                    asserts={test.operations.map(operation => operation.asserts || []).flat()}
                                    testReport={report}
                                    testScript={script}
                                    testStepHeaderOperation={test.id}
                                    testStepOperation={(test: any) => test.operation?.operation?.id ?? ''}
                                    testStepAssert={(assert: any) => assert.assert?.id ?? ''}
                                    isManualTest={isManualTest}
                                    titleSection={test.id}
                                    addTestOperation='testOperation'
                                    addTestAssert='testAssert'
                                    testId={test.id}
                                    onAddOperation={handleAddTest}
                                    onAddAssert={handleAddTestAssert}
                                    onError={onError}
                                    onDetail={onDetail}
                                    onUpdateTestReport={handleUpdateTestReport}
                                    existingOperationIds={existingOperationIds}
                                    setIsCreatingTest={setIsCreatingTest}
                                    refreshTestReport={readReport}
                                />
                            ))}
                        </Card.Body>
                    )}
                </Card>

                <TestOperationSection
                    operations={teardowns}
                    testReport={report}
                    testScript={script}
                    testStepHeaderOperation='Teardown'
                    testStepOperation={(teardown: any) => teardown.operation?.operation?.id ?? ''}
                    isManualTest={isManualTest}
                    titleSection='Teardown'
                    isTeardown={true}
                    addTestOperation='teardownOperation'
                    onAddOperation={handleAddTeardown}
                    onError={onError}
                    onDetail={onDetail}
                    onUpdateTestReport={handleUpdateTestReport}
                    showAddFaPlusIcon={true}
                    existingOperationIds={existingOperationIds}
                    refreshTestReport={readReport}
                />

            </>
        </PandoraPage >
    );
};

export default TestReportDetails;
